import { QueryResult, Reference } from '@apollo/client';
import { ApiAdminUser, AdminSorts, isAdminSorts } from '../types';
import { Role } from '@tendium/prom-types';
import { isObject, isString } from 'src/helpers';
import { AdminUsersData } from '.';

export enum AdminUsersSearchParams {
  email = 'email',
  companyId = 'companyId',
  roles = 'roles',
  sortBy = 'sortBy'
}
export type AdminUsersCached = {
  count: number;
  users: Reference[];
};
export type AdminUsersDataCached = {
  value: AdminUsersCached;
  variables: Omit<ApiAdminUsersVars, 'searchOptions'> & ApiAdminUsersSearchVars;
};
export type ApiAdminUsersSortVars = { field: string; direction: string };
export type ApiAdminUsersSearchVars = { email?: string; companyId?: string; roles?: Role[] };
export type ApiAdminUsersVars = ApiAdminUsersSearchVars & {
  amount: number;
  offset: number;
  sortBy?: ApiAdminUsersSortVars;
};

export interface AdminUsersSortVars {
  column: AdminUsersSortField;
  sort: AdminSorts;
}
export type AdminUsersSearchVars = ApiAdminUsersSearchVars;
export type AdminUsersVars = AdminUsersSearchVars & { sortBy?: AdminUsersSortVars };

export function isAdminUsersSorting(u: unknown): u is AdminUsersSortVars {
  return isObject(u) && 'sort' in u && 'column' in u && isAdminUsersSortField(u.column) && isAdminSorts(u.sort);
}
export function isAdminUsersSearch(u: unknown): u is AdminUsersSearchVars {
  return (
    isObject(u) &&
    (('email' in u && isString(u.email)) ||
      ('companyId' in u && isString(u.companyId)) ||
      ('roles' in u && Array.isArray(u.roles)))
  );
}

// changed according to rendered table, there is no company.featureRoles or user.role columns
const ADMIN_USERS_SORT_FIELDS = [
  'user.name',
  'user.email',
  'user.createdOn',
  'company.companyName',
  'company.id',
  'company.orgNumber',
  'company.pricePlan',
  'company.country',
  'user.isAdmin'
] as const;
export type AdminUsersSortField = (typeof ADMIN_USERS_SORT_FIELDS)[number];

export function isAdminUsersSortField(u: unknown): u is AdminUsersSortField {
  return (
    u === 'user.name' ||
    u === 'user.email' ||
    u === 'user.createdOn' ||
    u === 'company.companyName' ||
    u === 'company.id' ||
    u === 'company.orgNumber' ||
    u === 'company.pricePlan' ||
    u === 'company.country' ||
    u === 'user.isAdmin'
  );
}

export type UseAdminUsersVars = [
  { args: AdminUsersVars; vars: ApiAdminUsersVars; initArgs: AdminUsersVars },
  (args: Partial<AdminUsersVars>) => void
];
export interface ApiAdminUsersResponse {
  searchUser: ApiAdminUsersResponseData;
}
export interface ApiAdminUsersResponseData {
  count: number;
  users: ApiAdminUser[];
}
export interface UseAdminUsers extends Omit<QueryResult<ApiAdminUsersResponse, ApiAdminUsersVars>, 'data'> {
  data?: AdminUsersData;
  fetchingMore: boolean;
}
export const DEFAULT_PAGE = 1;
export const ADMIN_USERS_PAGE_SIZE = 15;
export const DEFAULT_ADMIN_USERS_VARS: AdminUsersVars = {
  sortBy: undefined,
  email: undefined,
  companyId: undefined,
  roles: undefined
};
